'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition
} from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { Button } from '@/components/ui/button';
import { usePathname } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { createTickMarkFormatter } from '@/lib/utils/createTickMarkFormatter';
import { createTimeFormatter } from '@/lib/utils/createTimeFormatter';
import { formatNumberWithCommas } from '@/lib/utils/formatNumberWithCommas';
import { getUserLocale } from '@/lib/utils/getUserLocale';
import { getUserTimezone } from '@/lib/utils/getUserTimezone';
import { updateUrlParams } from '@/lib/utils/updateUrlParams';
import { TF_LIST, TF_LIST_POOL } from '@/ts/constants/C_CHART';
import {
  C_DEFAULT_POOL_TIMEFRAME,
  C_DEFAULT_PREDICTION_TIMEFRAME
} from '@/ts/constants/C_CHART_DEFAULTS';

import type {
  T_CandlePoint,
  T_LinePoint,
  T_Symbol,
  T_Timeframe
} from '@/ts/types/T_Chart';
import type {
  ISeriesApi,
  IChartApi,
  AreaData,
  WhitespaceData,
  CandlestickData
} from 'lightweight-charts';

type T_AreaPriceChartProps = {
  symbol: T_Symbol;
  initialTimeframe?: T_Timeframe;
  initialData?: T_LinePoint[] | T_CandlePoint[];
  className?: string;
  type?: 'prediction' | 'pool';
  variant?: 'default' | 'modal';
  timeframe?: T_Timeframe;
  setStateTimeframe?: (timeframe: T_Timeframe) => void;
};

// Type guard to check if data item is a CandlePoint
function isCandlePoint(item: any): item is T_CandlePoint {
  return (
    item &&
    typeof item.open === 'number' &&
    typeof item.high === 'number' &&
    typeof item.low === 'number' &&
    typeof item.close === 'number'
  );
}

export default function AreaPriceChart({
  symbol,
  initialTimeframe = C_DEFAULT_PREDICTION_TIMEFRAME,
  initialData,
  className,
  type = 'prediction',
  variant,
  setStateTimeframe,
  timeframe
}: T_AreaPriceChartProps) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  // Using generic series ref to hold either Area or Candlestick series
  const seriesRef = useRef<
    ISeriesApi<'Area'> | ISeriesApi<'Candlestick'> | null
  >(null);
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();

  const tfList = type === 'prediction' ? TF_LIST : TF_LIST_POOL;
  // read tf from URL or fallback to initialTimeframe
  const [tf, setTf] = useState<T_Timeframe>(() => {
    if (variant === 'modal' && timeframe) {
      return timeframe;
    } else {
      const fromUrl = searchParams
        .get('tf')
        ?.toUpperCase() as T_Timeframe | null;
      return fromUrl && tfList.includes(fromUrl) ? fromUrl : initialTimeframe;
    }
  });

  const locale = useMemo(() => getUserLocale(), []);
  const tz = useMemo(() => getUserTimezone(), []);
  const timeFormatter = useMemo(
    () => createTimeFormatter(locale, tz),
    [locale, tz]
  );

  const tickMarkFormatter = useMemo(
    () => createTickMarkFormatter(locale, tz),
    [locale, tz]
  );

  /* Tooltip state */
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    top: number;
    left: number;
    price: string;
    date: string;
  } | null>(null);

  // create chart and seed initialData
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let isCancelled = false;
    let chartInstance: IChartApi | null = null;

    (async () => {
      // Dynamic import to avoid SSR issues
      const { createChart, ColorType, AreaSeries, CandlestickSeries } =
        await import('lightweight-charts');

      if (isCancelled || !el) return;

      const chart = createChart(el, {
        autoSize: true,
        layout: {
          background: { type: ColorType.Solid, color: 'var(--background)' },
          textColor: 'rgba(255,255,255,0.72)'
        },
        grid: {
          vertLines: { color: 'transparent' },
          horzLines: { color: 'hsla(0,0%,100%,0.1)' }
        },
        rightPriceScale: { visible: false },
        leftPriceScale: { visible: false },
        timeScale: {
          fixLeftEdge: true,
          fixRightEdge: true,
          borderVisible: false,
          timeVisible: true,
          tickMarkFormatter
        },
        crosshair: {
          horzLine: {
            visible: false,
            labelVisible: false
          },
          vertLine: {
            visible: true,
            style: 0,
            width: 1,
            color: 'rgba(255, 255, 255, 0.1)',
            labelVisible: false
          }
        }
      });

      chartInstance = chart;

      // Determine series type based on initialData structure
      const hasCandleData =
        initialData && initialData.length > 0 && isCandlePoint(initialData[0]);

      let series;

      if (hasCandleData) {
        series = chart.addSeries(CandlestickSeries, {
          upColor: '#10b77f',
          downColor: '#ef4343',
          borderVisible: false,
          wickUpColor: '#10b77f',
          wickDownColor: '#ef4343'
        });
      } else {
        series = chart.addSeries(AreaSeries, {
          lineWidth: 2,
          topColor: 'hsla(160,84%,39%,0.4)',
          bottomColor: 'transparent',
          lineColor: 'hsla(160,84%,39%,1)'
        });
      }

      chartRef.current = chart;
      seriesRef.current = series;

      if (initialData) {
        if (hasCandleData) {
          (series as ISeriesApi<'Candlestick'>).setData(
            initialData as CandlestickData[]
          );
        } else {
          (series as ISeriesApi<'Area'>).setData(
            initialData as (AreaData | WhitespaceData)[]
          );
        }
        chart.timeScale().fitContent();
      }

      chart.subscribeCrosshairMove(param => {
        if (
          param.point === undefined ||
          !param.time ||
          param.point.x < 0 ||
          param.point.x > el.clientWidth ||
          param.point.y < 0 ||
          param.point.y > el.clientHeight
        ) {
          setTooltip(null);
        } else {
          const data = param.seriesData.get(series) as any;
          if (data) {
            // Check for Candle vs Area data
            let priceVal = 0;
            if (typeof data.close === 'number') {
              priceVal = data.close;
            } else if (typeof data.value === 'number') {
              priceVal = data.value;
            }

            const dateStr = timeFormatter(Number(param.time));

            let decimals = 2;
            const priceValStr = priceVal.toString();
            if (priceValStr.includes('.')) {
              const fractionalDigits = priceValStr.split('.')[1].length;
              // If incoming decimals > 2, use 4 decimals.
              if (fractionalDigits > 2) {
                decimals = 4;
              }
            }

            const priceStr = formatNumberWithCommas(priceVal, decimals);

            const toolTipWidth = 100;
            const toolTipHeight = 80;
            const toolTipMargin = 15;

            const coordinate = series.priceToCoordinate(priceVal);
            let shiftedTop = 0;
            if (coordinate !== null) {
              shiftedTop = coordinate - toolTipHeight - toolTipMargin;
              if (shiftedTop < 0) {
                shiftedTop = coordinate + toolTipMargin;
              }
            }

            const left = Math.max(
              0,
              Math.min(
                el.clientWidth - toolTipWidth,
                param.point.x - toolTipWidth / 2
              )
            );

            setTooltip({
              visible: true,
              top: shiftedTop,
              left: left,
              price: priceStr,
              date: dateStr
            });
          } else {
            setTooltip(null);
          }
        }
      });
    })();

    return () => {
      isCancelled = true;
      if (chartInstance) {
        chartInstance.remove();
        chartInstance = null;
      }
    };
  }, [initialData, symbol, tf, tickMarkFormatter, timeFormatter]);

  const handleTfChange = (time: T_Timeframe) => {
    if (time === tf) return;
    setTf(time);

    if (variant === 'modal') {
      setStateTimeframe?.(time);
    } else {
      const newParams = updateUrlParams(searchParams, { tf: time });
      const defaultTf =
        type === 'pool'
          ? C_DEFAULT_POOL_TIMEFRAME
          : C_DEFAULT_PREDICTION_TIMEFRAME;

      if (time === defaultTf) {
        router.replace(pathname);
      } else {
        router.replace(`?${newParams}`);
      }
    }
  };

  return (
    <div className={cn('w-full flex flex-col', className)}>
      <div
        ref={containerRef}
        className="relative h-49 md:h-61.5 w-full rounded-2xl bg-background/40 ring-1 ring-white/5"
      >
        {tooltip && tooltip.visible && (
          <div
            className="absolute z-50 pointer-events-none rounded-lg bg-card border border-border p-2 text-xs shadow-lg flex flex-col items-center gap-1 min-w-[100px]"
            style={{
              top: tooltip.top,
              left: tooltip.left
            }}
          >
            <div className="text-muted-foreground font-medium">
              {tooltip.date}
            </div>
            <div className="text-foreground font-bold text-sm">
              ${tooltip.price}
            </div>
          </div>
        )}
      </div>
      <div className="py-3 px-4 flex gap-0 sm:gap-3 w-full max-w-[var(--container-width)] self-center justify-center bg-background">
        {tfList.map(time => {
          const active = tf === time;
          return (
            <Button
              variant="ghost"
              key={time}
              disabled={isPending}
              aria-pressed={active}
              className={cn(
                'w-14 md:w-full px-4 py-1 rounded-full text-base font-medium hover:bg-card',
                active ? 'bg-card text-foreground font-bold' : ''
              )}
              onClick={() =>
                !active && startTransition(() => handleTfChange(time))
              }
            >
              {time}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
